import React, { useState } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import {
  RiAddLine as PlusIcon,
  RiFileListLine as DocumentIcon,
  RiEditLine as EditIcon,
  RiDeleteBinLine as TrashIcon,
  RiCalendarLine as CalendarIcon,
  RiAlertLine as AlertIcon,
} from 'react-icons/ri';

interface UscisRequest {
  id: string;
  type: string;
  receivedDate: string;
  dueDate: string;
  description: string;
  status: 'New' | 'In Progress' | 'Completed';
  analysis?: string;
  responseDraft?: string;
}

const REQUEST_TYPES = [
  'Request for Evidence (RFE)',
  'Notice of Intent to Deny (NOID)',
  'Motion to Reopen / Reconsider',
  'Other',
];

const REQUEST_STATUSES = ['New', 'In Progress', 'Completed'];

interface UscisRequestsTabProps {
  caseId: string;
}

const UscisRequestsTab: React.FC<UscisRequestsTabProps> = ({ caseId }) => {
  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list');
  const [requests, setRequests] = useState<UscisRequest[]>([]);
  const [editingRequest, setEditingRequest] = useState<UscisRequest | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    type: REQUEST_TYPES[0],
    receivedDate: '',
    dueDate: '',
    description: '',
    status: 'New' as 'New' | 'In Progress' | 'Completed',
    analysis: '',
    responseDraft: '',
  });

  const handleCreate = () => {
    setMode('create');
    setFormData({
      type: REQUEST_TYPES[0],
      receivedDate: '',
      dueDate: '',
      description: '',
      status: 'New',
      analysis: '',
      responseDraft: '',
    });
  };

  const handleEdit = (request: UscisRequest) => {
    setEditingRequest(request);
    setFormData({
      type: request.type,
      receivedDate: request.receivedDate,
      dueDate: request.dueDate,
      description: request.description,
      status: request.status,
      analysis: request.analysis || '',
      responseDraft: request.responseDraft || '',
    });
    setMode('edit');
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this USCIS request?')) {
      setRequests(requests.filter(r => r.id !== id));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'create') {
      const newRequest: UscisRequest = {
        id: Date.now().toString(),
        ...formData,
      };
      setRequests([newRequest, ...requests]);
    } else if (mode === 'edit' && editingRequest) {
      setRequests(requests.map(r => (r.id === editingRequest.id ? { ...r, ...formData } : r)));
    }

    setMode('list');
    setEditingRequest(null);
  };

  const handleCancel = () => {
    setMode('list');
    setEditingRequest(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New':
        return 'bg-blue-100 text-blue-800';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Completed':
        return 'bg-success-100 text-success-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  const getDaysUntilDue = (dueDate: string) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (mode === 'create' || mode === 'edit') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">
            {mode === 'create' ? 'Add New USCIS Request' : 'Edit USCIS Request'}
          </h2>
        </div>

        <Card padding="lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Request Type */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Request Type <span className="text-error-600">*</span>
              </label>
              <select
                value={formData.type}
                onChange={e => setFormData({ ...formData, type: e.target.value })}
                required
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {REQUEST_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Date Received"
                type="date"
                value={formData.receivedDate}
                onChange={e => setFormData({ ...formData, receivedDate: e.target.value })}
                required
                fullWidth
              />
              <Input
                label="Due Date"
                type="date"
                value={formData.dueDate}
                onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                required
                fullWidth
              />
            </div>

            {/* Description */}
            <Textarea
              label="Description"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Describe the request from USCIS..."
              fullWidth
            />

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={e => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {REQUEST_STATUSES.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Analysis */}
            <Textarea
              label="Analysis"
              value={formData.analysis}
              onChange={e => setFormData({ ...formData, analysis: e.target.value })}
              rows={4}
              placeholder="Analyze the request points and requirements..."
              helperText="Optional. Your analysis of what USCIS is asking for."
              fullWidth
            />

            {/* Response Draft */}
            <Textarea
              label="Response Draft"
              value={formData.responseDraft}
              onChange={e => setFormData({ ...formData, responseDraft: e.target.value })}
              rows={4}
              placeholder="Draft your response strategy..."
              helperText="Optional. Your planned response to this request."
              fullWidth
            />

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-neutral-200">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">
                {mode === 'create' ? 'Create Request' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-neutral-900">USCIS Requests</h2>
        <Button onClick={handleCreate} className="inline-flex items-center gap-2">
          <PlusIcon className="h-4 w-4" />
          Add Request
        </Button>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <DocumentIcon className="mx-auto h-12 w-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">No USCIS requests</h3>
            <p className="mt-1 text-sm text-neutral-500">
              Get started by creating a new USCIS request.
            </p>
            <div className="mt-6">
              <Button onClick={handleCreate} className="inline-flex items-center gap-2">
                <PlusIcon className="h-4 w-4" />
                Add Request
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map(request => {
            const daysUntilDue = getDaysUntilDue(request.dueDate);
            const isUrgent = daysUntilDue <= 14 && request.status !== 'Completed';

            return (
              <Card key={request.id} padding="md" className="hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <DocumentIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      <h3 className="text-base font-semibold text-neutral-900">
                        {request.type}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}
                      >
                        {request.status}
                      </span>
                    </div>

                    {request.description && (
                      <p className="text-sm text-neutral-700 mb-3">{request.description}</p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-neutral-600">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        <span>Received: {new Date(request.receivedDate).toLocaleDateString()}</span>
                      </div>
                      <div
                        className={`flex items-center gap-1 ${isUrgent ? 'text-error-600 font-medium' : ''}`}
                      >
                        {isUrgent && <AlertIcon className="h-3.5 w-3.5" />}
                        <span>Due: {new Date(request.dueDate).toLocaleDateString()}</span>
                        {daysUntilDue > 0 && request.status !== 'Completed' && (
                          <span className={isUrgent ? 'font-semibold' : ''}>
                            ({daysUntilDue} {daysUntilDue === 1 ? 'day' : 'days'} remaining)
                          </span>
                        )}
                      </div>
                    </div>

                    {request.analysis && (
                      <div className="mt-3 p-3 bg-neutral-50 rounded-md">
                        <h4 className="text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-1">
                          Analysis
                        </h4>
                        <p className="text-sm text-neutral-700">{request.analysis}</p>
                      </div>
                    )}

                    {request.responseDraft && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-md">
                        <h4 className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-1">
                          Response Draft
                        </h4>
                        <p className="text-sm text-blue-900">{request.responseDraft}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(request)}
                      className="inline-flex items-center gap-1"
                    >
                      <EditIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(request.id)}
                      className="inline-flex items-center gap-1 text-error-600 hover:text-error-700"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UscisRequestsTab;
